import { Suspense, useRef, useState } from "react";
import { Button, Col, Form, Stack } from "react-bootstrap";
import { Await, useRouteLoaderData } from "react-router-dom";
import { formatDaypartText } from "./HelperFunctions";
import { Boombox, CheckLg, PencilFill, TrashFill, XLg, CardText, Clock, Calendar3Week } from "react-bootstrap-icons";
import { axiosInstance } from "../auth/Auth";
import TimePicker from "./Pickers/TimePicker";
import ConfirmPrompt from "./ConfirmPrompt";
import DaysPicker from "./Pickers/DaysPicker";

export default function DaypartRow({ daypart, onDelete, onEdit }) {
    const moduleName = `daypart-${daypart.daypart_id}`;

    const { radios } = useRouteLoaderData("root");
    const [editing, setEditing] = useState(false);
    const [daypartName, setDaypartName] = useState(daypart.name);
    const [showConfirm, setShowConfirm] = useState(false);

    const inputRef = useRef(null);

    async function handleEditDaypart(event) {
        event.preventDefault();
        try {
            const modifiedDaypart = { ...daypart, name: daypartName };
            await axiosInstance.put(`dayparts/${daypart.daypart_id}`, modifiedDaypart);
            onEdit(daypart.daypart_id, modifiedDaypart);
            setEditing(false);
        } catch (error) {}
    }

    async function handleDeleteDaypart() {
        try {
            await axiosInstance.delete(`dayparts/${daypart.daypart_id}`);
            onDelete(daypart.daypart_id);
        } catch (error) {
            if (error.response?.status === 404) onDelete(daypart.daypart_id);
        }
    }

    function handleEditPress() {
        setEditing(true);
        inputRef.current.focus();
    }

    function handleDiscard() {
        setEditing(false);
        setDaypartName(daypart.name);
    }

    function handleKeyPress(e) {
        if (e.key === "Escape") {
            handleDiscard();
        }
    }

    return (
        <Form onSubmit={handleEditDaypart} className="row p-0 py-2 py-lg-0 m-0 align-items-center">
            <ConfirmPrompt
                bodyText="Are you sure you want to delete daypart?"
                cancelText="No, go back"
                handleConfirm={handleDeleteDaypart}
                handleClose={() => setShowConfirm(false)}
                show={showConfirm}
            />
            <Col xs lg={2} className="order-first">
                <Stack direction="horizontal" gap={1}>
                    <span className=" d-flex d-lg-none">
                        <Boombox width={18} height={18} />
                    </span>
                    <Suspense>
                        <Await resolve={radios}>
                            {resolvedRadios => <span>{resolvedRadios.data.find(x => x.radio_id === daypart.radio_id)?.radio_name}</span>}
                        </Await>
                    </Suspense>
                </Stack>
            </Col>
            <Col sm={12} className=" d-lg-none"></Col>
            <Col xs={12} sm={8} lg={2}>
                <Stack direction="horizontal" gap={1}>
                    <span className=" d-flex d-lg-none">
                        <CardText width={18} height={18} />
                    </span>
                    <Form.Control
                        ref={inputRef}
                        type="text"
                        size="sm"
                        className="py-0"
                        readOnly={!editing}
                        plaintext={!editing}
                        onKeyDown={handleKeyPress}
                        value={editing ? daypartName ?? "" : daypartName ?? formatDaypartText(daypart)}
                        onChange={e => setDaypartName(e.currentTarget.value)}
                        placeholder="Daypart name"
                    />
                    {editing ? (
                        <>
                            <Button type="submit" variant="success" size="sm" className="p-1 d-flex">
                                <CheckLg className="d-none d-lg-inline-block" height={20} width={20} />
                                <CheckLg className=" d-lg-none" height={18} width={18} />
                            </Button>
                            <Button variant="danger" size="sm" className="p-1 d-flex" onClick={handleDiscard}>
                                <XLg className="d-none d-lg-inline-block" height={20} width={20} />
                                <XLg className=" d-lg-none" height={18} width={18} />
                            </Button>
                        </>
                    ) : (
                        <Button variant="ra-yellow" size="sm" className="p-1 d-flex" onClick={handleEditPress}>
                            <PencilFill className="d-none d-lg-inline-block" height={20} width={20} />
                            <PencilFill className=" d-lg-none" height={18} width={18} />
                        </Button>
                    )}
                </Stack>
            </Col>
            <Col className=" justify-content-center d-flex" xs={"auto"} sm={2} lg={1}>
                <Stack direction="horizontal" gap={1}>
                    <span className=" d-flex d-lg-none">
                        <Clock width={18} height={18} />
                    </span>
                    <TimePicker
                        size="sm"
                        formData={{ startTime: new Date(new Date().toDateString() + " " + daypart.start_time) }}
                        readOnly
                        plaintext
                        name={`${moduleName}-edit-start`}
                        formKey="startTime"
                        onlyInput
                    />
                </Stack>
            </Col>
            <Col className=" justify-content-center d-flex" xs={"auto"} sm={2} lg={1}>
                <Stack direction="horizontal" gap={1}>
                    <span className=" d-flex d-lg-none">
                        <Clock width={18} height={18} />
                    </span>
                    <TimePicker
                        size="sm"
                        formData={{ endTime: new Date(new Date().toDateString() + " " + daypart.end_time) }}
                        readOnly
                        plaintext
                        name={`${moduleName}-edit-end`}
                        formKey="endTime"
                        seconds="59"
                        onlyInput
                    />
                </Stack>
            </Col>
            <Col xs={12} sm className="d-flex">
                <Stack direction="horizontal" gap={1} className=" flex-grow-1">
                    <span className=" d-none d-sm-flex d-lg-none">
                        <Calendar3Week width={18} height={18} />
                    </span>
                    <DaysPicker size="sm" fluid={"sm"} readOnly={true} formData={{ days: daypart.weekdays }} name={moduleName} onlyInput />
                </Stack>
            </Col>
            <Col xs={"auto"} className=" order-first order-sm-last">
                <Button variant="danger" size="sm" className=" p-1 d-flex" onClick={() => setShowConfirm(true)}>
                    <TrashFill className="d-none d-lg-inline-block" height={20} width={20} />
                    <TrashFill className=" d-lg-none" height={18} width={18} />
                </Button>
            </Col>
        </Form>
    );
}
